<template>
  <full-page id="fullpage"
             ref="fullpage"
             v-if="!showPortfolioCreationLoadingScreen"
             :options="options">

    <!-- it might sound weird to implement a dialog in a full-page-section, but this is correct. This way, the
    content of the dialog is scrollable and the page behaves like the rest of the onboarding with the
    fullpage-behaviour -->
    <fin-full-page-section v-if="fillBucket"
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled">
      <fin-dialog v-model="showEditDialog" class="d-flex align-center" fullscreen hide-close-button persistent dense>
        <fin-fill-portfolio v-if="displayMode === 'selection'"
                            v-model="selectedBucket"
                            :assetType="fillBucket"
                            :extendedView="true"
                            @cancel="cancelFillBucket()"
                            @input="showDistributionScreen()"
        />
        <fin-portfolio-distribution
          v-else
          v-model="selectedBucket"
          :asset-class="fillBucket"
          :capital="initialCapital * assetDistribution.find(item => item.name.id === fillBucket.id).percentage / 100"
          @save-bucket-distribution="saveBucketDistribution"
          @switch-to-selection="displayMode = 'selection'"
        />
      </fin-dialog>
    </fin-full-page-section>


    <fin-full-page-section v-else
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled"
                           :style="'padding-bottom: ' + finStrategistPaddingBottom + 'px'">
      <v-row class="justify-center px-3 pt-3">
        <v-col align="center" class="onboarding-portfolio-col" cols="12" lg="7" md="8">
          <h1>
            <fin-lexicon-text :text="onboardingMode ? 'Dein erstes Wertpapierportfolio' : 'Dein Portfolio'"
                              auto-solve/>
          </h1>
          <p class="mt-5">
            <fin-lexicon-text auto-solve
                              text="Jetzt wird es konkret. Finde zu jeder Assetklasse, die zu deiner Strategie passenden Wertpapiere. Dazu gibt es zwei Wege, über deine individuelle Auswahl aufgrund eigener Recherche oder mit Hilfe unserer Musterportfolios."/>
          </p>

          <v-row class="justify-center mb-3">
            <v-col cols="auto pb-0">
              <fin-lexicon-text text="So erstellst du dein erstes Musterportfolio"
                                auto-solve/>
              <fin-video-dialog v-model="videoDialog"
                                :src="videoSrc"/>
            </v-col>
            <v-col align="center" cols="auto">
              <div class="thumbnail-wrapper">
                <v-img src="@/assets/onboarding/onboarding_preview_img.jpg" style="position:relative;"
                       @click="videoDialog=true">
                  <template #placeholder>
                    <v-row class="fill-height ma-0"
                           align="center"
                           justify="center">
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"/>
                    </v-row>
                  </template>
                </v-img>
                <v-icon class="play-icon">mdi-play-circle-outline</v-icon>
              </div>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col
              :style="{display: 'flex', alignItems: 'center', flexDirection: 'column' }">
              <v-radio-group v-model="portfolioWay" class="justify-center mt-0"
                             mandatory
                             :column="$vuetify.breakpoint.xs"
                             @change="showConfirmRadioButtonChanged">
                <div style="display: inline-flex;">
                  <v-radio :key="1" :value="1">
                    <template #label>
                    <span :style="{ color: $finColors.primaryDark, maxWidth: '350px', marginLeft: '4px' }"
                          class="radio-button-text mb-0 pb-0">
                      <span style="font-weight: bold;">Eigene Auswahl</span>
                    </span>
                    </template>
                  </v-radio>
                  <v-tooltip :max-width="350" bottom>
                    <template #activator="{ on, attrs }">
                      <v-icon :class="$vuetify.breakpoint.xs ? 'ml-3 mb-2' : 'ml-3'"
                              :color="$finColors.primaryDark"
                              v-bind="attrs"
                              v-on="on">
                        icon-information
                      </v-icon>
                    </template>
                    <span>Du selbst triffst eine individuelle Auswahl aus denen zu deiner Strategie passenden und vorselektierten Wertpapieren.</span>
                  </v-tooltip>
                </div>

                <div style="display: inline-flex;">
                  <v-radio :key="2" :class="$vuetify.breakpoint.xs ? '' : 'ml-15'" :value="2">
                    <template #label>
                    <span :style="{ color: $finColors.primaryDark, maxWidth: '350px', marginLeft: '4px' }"
                          class="radio-button-text mb-0 pb-0">
                      <span style="font-weight: bold;">Musterportfolio</span>
                    </span>
                    </template>
                  </v-radio>
                  <v-tooltip :max-width="350" bottom>
                    <template #activator="{ on, attrs }">
                      <v-icon :class="$vuetify.breakpoint.xs ? 'ml-3 mb-2' : 'ml-3'"
                              :color="$finColors.primaryDark"
                              v-bind="attrs"
                              v-on="on">
                        icon-information
                      </v-icon>
                    </template>
                    <span>Du hast nicht die Zeit oder (noch) nicht die Expertise eine individuelle Auswahl aus denen zu deiner Strategie passenden Wertpapiere zu treffen. Du übernimmst unseren Vorschlag eines Musterportfolio.</span>
                  </v-tooltip>
                </div>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row class="bucket-assetclass-wrapper mb-3" dense>
            <v-col v-for="(item, index) in usedAssetDistribution" :key="index"
                   align="center">
              <p class="mt-5 mb-1 bucket-assetclass">
                <fin-lexicon-text :text="item.percentage + '% ' + assetClassLabel(item.name)"
                                  auto-solve></fin-lexicon-text>
              </p>
              <fin-shapes-collection-container :fill="item.name.color"
                                               :full-value="assetDistributionMax"
                                               :percentage="item.percentage"
                                               :percentage-used="assignedCapital[item.name.id] / getAssetTotal(item.percentage) * 100"
                                               :stroke="item.name.strokeColor"
                                               :style="$vuetify.breakpoint.mdAndDown ? 'max-width: 101px; max-height: 76.5px;' : $vuetify.breakpoint.lg ? 'max-width: 170px; max-height: 129px;' : ''"
              />
              <p class="mt-3 mb-0 bucket-actual">{{ assignedCapital[item.name.id] | finCurrency }}</p>
              <p class="mt-1 mb-0 bucket-total">von {{ getAssetTotal(item.percentage) | finCurrency }}</p>
              <v-btn class="start-button mt-3" :id="'onboarding-start-button-' + index" dark tile
                     :disabled="showSwitchConfirm ? portfolioWay === 1 : portfolioWay === 2"
                     @click="editValues(item)">
                Bearbeiten
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="justify-center pa-3">
        <v-col align="center" class="pb-12" cols="12" sm="10" md="8" lg="7">
          <v-btn :disabled="totalAssignedCapital<=0"
                 class="mb-2 mt-3 mb-12 portfolio-button"
                 outlined
                 tile
                 @click="showPortfolioContent">
            Auswahl anzeigen
          </v-btn>
          <v-btn :disabled="totalAssignedCapital<=0"
                 class="ml-2 mb-2 mt-3 mb-12 portfolio-button"
                 dark tile
                 @click="confirmPortfolio">
            Auswahl übernehmen
          </v-btn>
        </v-col>
      </v-row>
    </fin-full-page-section>

    <fin-full-page-section v-if="showPortfolioContentDialog"
                           :fullpageBehaviourDisabled="fullpageBehaviourDisabled">
      <fin-reduced-portfolio-details :selected-instruments="value.selectedInstruments"
                                     v-if="showPortfolioContentDialog"
                                     v-model="showPortfolioContentDialog"
                                     @closeReducedDetails="closePortfolioContentDialog"
                                     @closeReducedDetailsAndConfirmPortfolio="closePortfolioContentDialogAndConfirmPortfolio"/>
    </fin-full-page-section>
  </full-page>
</template>

<script>
import FinFullPageSection from '@/components/pages/onboarding/FullPageSection'
import FinLexiconText from '@/components/common/lexicon/LexiconText'
import FinFillPortfolio from '@/components/pages/onboarding/Step4/FillPortfolio'
import FinShapesCollectionContainer from '@/components/common/shapes/CollectionContainer'
import {FULL_PAGE_KEY} from '@/js/finConstants'
import FinPortfolioDistribution from '../../../common/portfolio/Distribution'
import AssetClassFinder from '../AssetClassFinder'
import {mapActions, mapGetters} from "vuex"
import finBrowser from "@/js/finBrowser"
import AmplitudePush from "@/mixins/AmplitudePush"
import FinVideoDialog from "@/components/common/video/VideoDialog"
import FinReducedPortfolioDetails from "@/components/pages/onboarding/Step4/ReducedPortfolioDetails"
import {finPortfolioAPI} from "@/js/finAPI"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import {EventBus} from "@/plugins/fincraft"
import PriceModel from "@/mixins/PriceModel"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {listUserPortfolios} from "@/components/common/GlobalSearch/graphql"

export default {
  name: 'fin-onboarding-step-4',
  mixins: [AmplitudePush, PriceModel],
  components: {
    FinReducedPortfolioDetails,
    FinVideoDialog,
    FinPortfolioDistribution,
    FinShapesCollectionContainer,
    FinFillPortfolio,
    FinFullPageSection,
    FinLexiconText,
  },
  props: {
    value: {},
    assetDistribution: {
      required: true
    },
    initialCapital: {
      required: true
    },
    createPortfolio: {
      type: Function,
      required: true
    },
    archetypeId: {
      type: String
    },
    createPortfolioError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const value = Object.assign({
      activeStep: 3,
      selectedInstruments: {},
      assignedCapital: {
        SHARE: 0,
        SHARE_ETF: 0,
        CORPORATE_BONDS_ETF: 0,
        GOVERNMENT_BONDS_ETF: 0,
        COMMODITIES_ETF: 0
      },
      portfolioId: null
    }, this.$props.value)
    return {
      videoDialog: false,
      videoSrc: "https://www.youtube.com/embed/JYODVLrL7FU",
      options: {
        licenseKey: FULL_PAGE_KEY,
        controlArrows: true,
        onLeave: this.onLeavePage,
        paddingTop: '0px',
        paddingBottom: '0px',
        responsiveWidth: 0,
        responsiveHeight: 0,
        scrollOverflow: false
        // scrollOverflowReset: true,// only available with the fullpage-extensions!!
      },
      defaultPortfolio: undefined,
      fillBucket: null,
      selectedBucket: [],
      initialShareComposition: [],
      displayMode: 'selection',
      model: value,
      showEditDialog: false,
      onboardingMode: true,
      mode: undefined,
      illuHeight: 0,
      illuWidth: 0,
      marginIlluTop: 15,
      fullpageBehaviourDisabled: false,
      portfolioWay: 2,
      musterportfolioData: undefined,
      showPortfolioContentDialog: false,
      showSwitchConfirm: false,
      showPortfolioCreationLoadingScreen: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['onboardingArchetypeID', 'userId']),
    assetDistributionMax() {
      return Math.max.apply(Math, this.assetDistribution.map(o => o.percentage))
    },
    selectedInstruments() {
      return this.model.selectedInstruments
    },
    assignedCapital() {
      return this.model.assignedCapital
    },
    totalAssignedCapital() {
      return this.model.assignedCapital.SHARE +
        this.model.assignedCapital.SHARE_ETF +
        this.model.assignedCapital.CORPORATE_BONDS_ETF +
        this.model.assignedCapital.GOVERNMENT_BONDS_ETF +
        this.model.assignedCapital.COMMODITIES_ETF
    },
    usedAssetDistribution() {
      return this.assetDistribution.filter(item => item.percentage !== 0)
    },
    onboardingPaddingTop() {
      let h = document.querySelector('.fin-onboarding-slider')?.getBoundingClientRect().height
      this.$set(this.options, 'paddingTop', (h ? h : 0) + 'px')
      return h
    },
    finStrategistPaddingTop() {
      let h = document.querySelector('.fin-top-navigation')?.getBoundingClientRect().height
      this.$set(this.options, 'paddingTop', (h ? h : 0) + 'px')
      return h
    },
    finStrategistPaddingBottom() {
      // needed because of fixed footer in finProfiler-Mode
      let h = document.querySelector('.v-footer--fixed')?.getBoundingClientRect().height * 2
      if (this.$vuetify.breakpoint.xs) {
        let hMobileNavFooter =
          document.querySelector('.v-bottom-navigation')?.getBoundingClientRect().height * 2
        h = hMobileNavFooter
      }
      this.$set(this.options, 'paddingBottom', (h ? h : 0) + 'px')
      return h
    },
    minHeightLoadingContainer() {
      const height = window.innerHeight
      const app = this.$vuetify.application
      const vmainHeight = app.top + app.bottom + app.footer
      const remainingSpaceWithoutScrollbars = height - vmainHeight

      return remainingSpaceWithoutScrollbars + 'px'
    },
  },
  methods: {
    ...mapActions('auth', ['updateArchetype']),
    showDistributionScreen() {
      this.displayMode = 'distribution'
    },
    cancelFillBucket() {
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_portfolio_fill_bucket_cancelled',
        'step': this.mode + '_portfolio_view_buckets',
        'step_choice': this.fillBucket.name,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.fillBucket = null
    },
    onLeavePage(origin, destination, direction) {
      this.$emit('pageChange', destination.index)
    },
    scrollDown() {
      this.$refs.fullpage?.api.silentMoveTo(this.$refs.fullpage?.$el.querySelectorAll('.section').length)
    },
    editValues(item) {
      this.fillBucket = item.name
      this.selectedBucket = this.selectedInstruments[this.fillBucket.id]
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_portfolio_fill_bucket_starting',
        'step': this.mode + '_portfolio_fill_bucket',
        'step_choice': this.fillBucket.name,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      this.showEditDialog = true
    },
    assetClassLabel(assetClass) {
      if (assetClass.id === 'COMMODITIES_ETF') {
        return 'Rohstoff-ETF'
      } else {
        return assetClass.name
      }
    },
    assetClass(id) {
      return AssetClassFinder.findById(id)
    },
    getAssetTotal(percentage) {
      const num = this.initialCapital * percentage / 100
      if (!num) {
        return 0
      }
      return num
    },
    nextDynamicPage() {
      this.$nextTick(() => {
        this.$refs.fullpage.build()
        this.$refs.fullpage.api.moveSectionDown()
      })
    },
    showPortfolioContent() {
      this.showPortfolioContentDialog = true
    },
    closePortfolioContentDialog() {
      this.showPortfolioContentDialog = false
    },
    closePortfolioContentDialogAndConfirmPortfolio() {
      this.showPortfolioContentDialog = false
      this.confirmPortfolio()
    },
    confirmPortfolio() {
      this.$confirm({
        title: 'Willst du deine Wertpapierauswahl in dein Portfolio übernehmen?',
        titleWithLexikonLink: true,
        text: 'Wenn du deinen Entwurf übernimmst, werden die von dir gewählten Wertpapiere in dein Portfolio übernommen und „aktiv“ gesetzt. Damit hast du ab jetzt ein fertiges finCraft Portfolio und kannst dessen Gewinnentwicklung verfolgen.',
        textWithLexikonLink: true,
        input: true,
        inputMultiline: true,
        inputLabel: 'Wie soll dein neues Portfolio heißen?',
        inputPlaceholder: 'Name meines Portfolios',
        confirmBtn: 'Ok, übernehmen'
      }).then(async (params) => {
          let result = await API.graphql(graphqlOperation(listUserPortfolios, {
            owner: this.userId
          }))
          if (this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.PORTFOLIO.id) &&
            result.data.listPortfoliosByOwner.items.length >= this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.PORTFOLIO.id)) {
            let newCount = result.data.listPortfoliosByOwner.items.length
            EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.PORTFOLIO.id, PRICEMODEL_FEATURE_RESTRICTIONS.QUANTITY, {itemAmount: ++newCount})
          } else {
            this.showPortfolioCreationLoadingScreen = true
            this.createPortfolio(params.inputText).then((portfolioId) => {
              this.$emit('portfolioSaved')
              this.$set(this.model, 'portfolioId', portfolioId)
              // save archetype during onboarding only
              // if called via strategy page -> the one already saved at profile is used
              if (this.$route.fullPath.includes('onboarding')) {
                this.updateArchetype(this.onboardingArchetypeID)
              }
              this.pushToAmplitude({
                'event': 'fincraft_' + this.mode + '_portfolio_saved',
                'step': this.mode + '_portfolio_view',
                'step_choice': this.portfolioWay === 1 ? 'portfolio_ownInstrumentsWay' : 'portfolio_samplePortfolioWay',
                'vue_breakpoint': this.$vuetify.breakpoint.name
              })

              this.$router.push({name: 'portfolioDetail', params: {id: portfolioId}})
              this.showPortfolioCreationLoadingScreen = false
            })
          }
      }).catch(() => {
      })
    },
    saveBucketDistribution(bucketAssignedCapital) {
      const newVal = Object.assign({}, this.model)
      newVal.selectedInstruments[this.fillBucket.id] = this.selectedBucket.filter((item => item.count > 0))
      newVal.selectedInstruments[this.fillBucket.id].forEach(i => i.instrument.assetClass = this.fillBucket.id)
      newVal.assignedCapital[this.fillBucket.id] = bucketAssignedCapital
      this.model = newVal
      this.fillBucket = null
      this.displayMode = 'selection'
      this.pushToAmplitude({
        'event': 'fincraft_' + this.mode + '_portfolio_distribution_saved',
        'step': this.mode + '_portfolio_view_buckets',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    calcIlluSize() {
      const svgWidth = 662
      const svgHeight = 381
      this.illuWidth = Math.min(800, document.body.clientWidth) * 85 / 100
      this.illuHeight = (this.illuWidth * svgHeight / svgWidth)
    },
    showConfirmRadioButtonChanged() {
      this.showSwitchConfirm = true
      this.$confirm({
        title: this.portfolioWay === 1 ? 'Zur individuellen Wertpapierauswahl wechseln?' : 'Zum finCraft Vorschlag wechseln?',
        titleWithLexikonLink: true,
        text: this.portfolioWay === 1 ? 'Die finCraft Vorschläge werden gelöscht, damit du deine individuelle Auswahl an Wertpapieren zusammenstellen kannst.' : 'Deine bisherige Auswahl an Wertpapieren wird gelöscht und durch die finCraft Vorschläge ersetzt.',
        textWithLexikonLink: true,
        confirmBtn: 'Ja, wechseln'
      }).then(() => {
        // reset the portfolio instruments
        this.fillBucket = null
        if (this.portfolioWay === 1) {
          // the user chooses the instruments himself => reset portfolio
          const newVal = Object.assign({}, this.model)
          this.assetDistribution.forEach(ad => {
            newVal.selectedInstruments[ad.name.id] = []
            newVal.assignedCapital[ad.name.id] = 0
          })
          this.model = newVal
        } else if (this.portfolioWay === 2) {
          // the user chooses to fill his portfolio with the instruments proposed in the 'musterportfolio'
          // get instruments from the backend and fill the buckets accordingly
          this.fillBucketsWithSamplePortfolioData()
        }
      }).catch(() => {
        this.portfolioWay = this.portfolioWay === 2 ? 1 : 2
      }).finally(() => {
        this.showSwitchConfirm = false
      })
    },
    async getSamplePortfolioData(capital, archetype, assetDistribution) {
      const assetDistributionMapped = assetDistribution.map(item => {
        return {name: item.name.id, percentage: item.percentage}
      })

      const samplePortfolioInput = {cash: capital, archetype: archetype, assetDistribution: assetDistributionMapped}

      console.log('samplePortfolioInput', samplePortfolioInput)
      const musterP = await finPortfolioAPI.getSamplePortfolio(samplePortfolioInput)

      if (musterP) {
        return musterP
      }
    },
    async fillBucketsWithSamplePortfolioData() {
      if (this.musterportfolioData === undefined) {
        const samplePortf = await this.getSamplePortfolioData(this.initialCapital, this.archetypeId, this.assetDistribution)

        if (samplePortf) {
          const newVal = Object.assign({}, this.model)

          for (const [key, value] of Object.entries(samplePortf)) {
            newVal.selectedInstruments[key] = value.instruments
            newVal.assignedCapital[key] = value.assignedCapital
          }

          this.musterportfolioData = samplePortf

          this.model = newVal
        }
      } else {
        const newVal = Object.assign({}, this.model)

        for (const [key, value] of Object.entries(this.musterportfolioData)) {
          newVal.selectedInstruments[key] = value.instruments
          newVal.assignedCapital[key] = value.assignedCapital
        }

        this.model = newVal
      }
    }
  },
  watch: {
    model: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true
    },
    createPortfolioError() {
      if (this.createPortfolioError) {
        this.showPortfolioCreationLoadingScreen = false
        this.$emit('errorShown')
      }
    }
  },
  mounted() {
    // disable fullpage behaviour in mobiles and tablets
    this.fullpageBehaviourDisabled = finBrowser.IS_MOBILE_OR_TABLET || finBrowser.IS_IOS_OR_ANDROID_APP
    this.$set(this.options, 'responsiveWidth', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'responsiveHeight', this.fullpageBehaviourDisabled ? 5000 : 0)
    this.$set(this.options, 'scrollOverflow', !this.fullpageBehaviourDisabled)

    this.$set(this.options, 'paddingTop', this.onboardingPaddingTop + 'px')

    if (this.$route.fullPath.includes('finStrategist')) {
      this.$set(this.options, 'paddingBottom', this.finStrategistPaddingBottom)
    }

    this.onboardingMode = !this.$route.fullPath.includes('finStrategist')
    this.mode = this.$route.fullPath.includes('finStrategist') ? 'finStrategist' : 'onboarding'

    this.fillBucketsWithSamplePortfolioData()

    this.scrollDown()
  },
  destroyed() {
    //workaround for FINDEV-2514
    document.body.style.overflow = null
  },
}
</script>

<style scoped lang="scss">
@import "../../../../styles/variables";

.bucket-assetclass {
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  height: 40px;
}

.bucket-actual {
  font-size: 24px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
  text-align: center;
  color: $fin-color-primary-dark;


  @media only screen and (max-width: 600px) {
    font-size: 16px;
  }
}

.bucket-total {
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  letter-spacing: normal;
  text-align: center;
  color: #99a0a4;
}

.thumbnail-wrapper {
  position: relative;
  padding-top: 0;
  overflow: hidden;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, .5);
  cursor: pointer;
  max-width: 150px;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: $fin-color-primary-light;
  opacity: .75;
  font-size: 2.5em;
}

.video-description {
  font-size: 1.2em;
}

.radio-button-text {
  text-align: center;
}

.v-input--selection-controls .v-input__slot > .v-label, .v-input--selection-controls .v-radio > .v-label {
  height: unset;
}
</style>
